import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { SimilarQuoteBadge, SimilarQuotesDialog } from "@/components/SimilarQuotes";
import { fmtCurrency, fmtDateTime, STATUS_TR, quotePdfName } from "@/utils/format";
import {
  Warehouse as WhIcon, Factory, Package, Clock, Pencil, ArrowRightCircle, FileDown,
} from "lucide-react";
import AuditLogPanel from "@/components/AuditLogPanel";

const STATUS_COLORS = {
  draft: "bg-neutral-200 text-neutral-800 border-neutral-500",
  sent: "bg-yellow-100 text-yellow-800 border-yellow-800",
  approved: "bg-green-100 text-green-800 border-green-800",
  rejected: "bg-red-100 text-red-800 border-red-800",
  converted: "bg-[#0055FF] text-white border-[#0055FF]",
};

function AllocHint({ item, products }) {
  const p = products?.[item.product_id];
  if (!p) return <span className="text-neutral-400 text-xs">—</span>;
  const stock = Number(p.stock) || 0;
  const qty = Number(item.quantity) || 0;
  if (stock >= qty) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-mono uppercase border border-[#0055FF] text-[#0055FF]">
        <WhIcon size={10} /> Depo · Stok yeterli
      </span>
    );
  }
  const deficit = Math.max(0, qty - stock);
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-mono uppercase border border-[#FF3B30] text-[#FF3B30]">
      <Factory size={10} /> Üretim · {deficit} {p.unit || ""} üretilecek
    </span>
  );
}

export default function QuoteDetailsDialog({ quoteId, open, onOpenChange, onEdit, onConvert }) {
  const [quote, setQuote] = useState(null);
  const [products, setProducts] = useState({});
  const [loading, setLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState({});
  const [similar, setSimilar] = useState([]);
  const [compareOpen, setCompareOpen] = useState(false);

  const load = async () => {
    if (!quoteId) return;
    setLoading(true);
    try {
      const [q, p, s] = await Promise.all([
        api.get(`/quotes/${quoteId}`),
        api.get("/products"),
        api.get(`/quotes/${quoteId}/similar`).catch(() => ({ data: { quotes: [] } })),
      ]);
      setQuote(q.data);
      setProducts(Object.fromEntries(p.data.map((x) => [x.id, x])));
      setSimilar((s.data.quotes || []).filter((x) => x.id !== quoteId).map((x) => ({ ...x })));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (open) load();
    else {
      setQuote(null);
      setSelectedIds({});
      setSimilar([]);
      setCompareOpen(false);
    }
    /* eslint-disable-next-line */
  }, [open, quoteId]);

  // Teklif yüklendiğinde pending kalemleri varsayılan olarak seçili yap.
  // Anahtar olarak item.id (yoksa stabil index-tabanlı yedek) kullanılır;
  // böylece her checkbox state'i tamamen bağımsız kalır.
  const keyFor = (it, i) => it?.id || `__idx_${i}`;
  useEffect(() => {
    if (!quote?.items) return;
    const next = {};
    quote.items.forEach((it, i) => {
      if ((it.status || "pending") === "pending") {
        next[keyFor(it, i)] = true;
      }
    });
    setSelectedIds(next);
  }, [quote?.id, quote?.updated_at]);

  const toggleItem = (key) => setSelectedIds((s) => {
    const copy = { ...s };
    if (copy[key]) delete copy[key]; else copy[key] = true;
    return copy;
  });
  const selectAllPending = () => {
    const next = {};
    (quote?.items || []).forEach((it, i) => {
      if ((it.status || "pending") === "pending") next[keyFor(it, i)] = true;
    });
    setSelectedIds(next);
  };
  const clearSelection = () => setSelectedIds({});

  const pendingItems = (quote?.items || [])
    .map((it, i) => ({ it, key: keyFor(it, i) }))
    .filter(({ it }) => (it.status || "pending") !== "converted");
  const selectedCount = pendingItems.filter(({ key }) => selectedIds[key]).length;

  const canEdit = quote && quote.status !== "converted" && !(quote.items || []).some((it) => it.status === "converted");
  const canConvert = quote && pendingItems.length > 0 && (quote.status === "draft" || quote.status === "sent" || quote.status === "approved");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none border-2 border-[#09090B] bg-white sm:max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl tracking-tight" data-testid="quote-details-title">
            {quote?.quote_number || "Teklif Detayı"}
          </DialogTitle>
          <div><SimilarQuoteBadge similar={similar} onOpen={() => setCompareOpen(true)} testid="quote-details-similar-badge" /></div>
        </DialogHeader>
        <SimilarQuotesDialog quoteId={compareOpen ? quoteId : null} onClose={() => setCompareOpen(false)} />

        {loading || !quote ? (
          <div className="p-8 text-center text-sm font-mono text-neutral-500">Yükleniyor…</div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="border-2 border-[#09090B] p-3">
                <div className="text-[10px] font-mono uppercase tracking-widest text-neutral-500">Müşteri</div>
                <div className="font-bold text-sm">{quote.customer_name}</div>
              </div>
              <div className="border-2 border-[#09090B] p-3">
                <div className="text-[10px] font-mono uppercase tracking-widest text-neutral-500">Oluşturan</div>
                <div className="font-mono text-sm truncate">{quote.created_by || "-"}</div>
              </div>
              <div className="border-2 border-[#09090B] p-3">
                <div className="text-[10px] font-mono uppercase tracking-widest text-neutral-500">Tarih</div>
                <div className="font-mono text-sm">{fmtDateTime(quote.created_at)}</div>
              </div>
              <div className="border-2 border-[#09090B] p-3">
                <div className="text-[10px] font-mono uppercase tracking-widest text-neutral-500">Durum</div>
                <span className={`inline-block px-2 py-0.5 text-[10px] font-mono uppercase border ${STATUS_COLORS[quote.status] || "bg-neutral-100"}`}>
                  {STATUS_TR[quote.status] || quote.status}
                </span>
              </div>
              <div className="border-2 border-[#09090B] p-3 md:col-span-4" data-testid="quote-detail-payment-info">
                <div className="text-[10px] font-mono uppercase tracking-widest text-neutral-500">Ödeme Bilgisi</div>
                <div className="text-sm">
                  {quote.payment_info
                    ? <span className="font-semibold">{quote.payment_info}</span>
                    : <span className="text-neutral-400 italic">—</span>}
                </div>
              </div>
            </div>

            <div className="border-2 border-[#09090B]">
              <Table>
                <TableHeader>
                  <TableRow className="bg-neutral-100 border-b-2 border-[#09090B]">
                    <TableHead className="font-mono uppercase text-xs w-[42px]">
                      {canConvert && (
                        <input
                          type="checkbox"
                          checked={pendingItems.length > 0 && selectedCount === pendingItems.length}
                          onChange={(e) => (e.target.checked ? selectAllPending() : clearSelection())}
                          data-testid="quote-items-select-all"
                        />
                      )}
                    </TableHead>
                    <TableHead className="font-mono uppercase text-xs">SKU</TableHead>
                    <TableHead className="font-mono uppercase text-xs">Ürün</TableHead>
                    <TableHead className="font-mono uppercase text-xs text-right">Miktar</TableHead>
                    <TableHead className="font-mono uppercase text-xs text-right">B.Fiyat</TableHead>
                    <TableHead className="font-mono uppercase text-xs text-right">Toplam</TableHead>
                    <TableHead className="font-mono uppercase text-xs">Durum</TableHead>
                    <TableHead className="font-mono uppercase text-xs">Rota (tahmin)</TableHead>
                    <TableHead className="font-mono uppercase text-xs">Termin</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {quote.items.map((it, i) => {
                    const isConverted = (it.status || "pending") === "converted";
                    const itemKey = keyFor(it, i);
                    return (
                    <TableRow key={itemKey} data-testid={`quote-item-${i}`}>
                      <TableCell>
                        {!isConverted ? (
                          <input
                            type="checkbox"
                            checked={!!selectedIds[itemKey]}
                            onChange={() => toggleItem(itemKey)}
                            disabled={!canConvert}
                            data-testid={`quote-item-check-${i}`}
                          />
                        ) : (
                          <span className="text-neutral-400 text-xs">—</span>
                        )}
                      </TableCell>
                      <TableCell className="font-mono text-sm">{it.sku}</TableCell>
                      <TableCell>
                        <div className="font-semibold text-sm flex items-center gap-2">
                          <Package size={12} /> {it.product_name}
                        </div>
                        {it.notes && (
                          <div
                            className="mt-1 text-xs text-neutral-600 whitespace-pre-wrap italic border-l-2 border-neutral-300 pl-2"
                            data-testid={`quote-item-notes-${i}`}
                          >
                            {it.notes}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-right font-mono">{it.quantity} {it.unit}</TableCell>
                      <TableCell className="text-right font-mono text-sm">{fmtCurrency(it.unit_price)}</TableCell>
                      <TableCell className="text-right font-mono">{fmtCurrency(it.line_total)}</TableCell>
                      <TableCell>
                        {isConverted ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-mono uppercase bg-[#0055FF] text-white border-2 border-[#0055FF]" data-testid={`quote-item-status-${i}`}>
                            Dönüştürüldü{it.converted_order_number ? ` · ${it.converted_order_number}` : ""}
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-mono uppercase border-2 border-[#09090B] text-[#09090B] bg-white" data-testid={`quote-item-status-${i}`}>
                            Beklemede
                          </span>
                        )}
                      </TableCell>
                      <TableCell><AllocHint item={it} products={products} /></TableCell>
                      <TableCell>
                        {it.due_date ? (
                          <span className="inline-flex items-center gap-1 text-xs font-mono px-1.5 py-0.5 border border-neutral-300 text-neutral-600">
                            <Clock size={10} /> {it.due_date}
                          </span>
                        ) : (
                          <span className="text-neutral-400 text-xs font-mono">—</span>
                        )}
                      </TableCell>
                    </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="border-2 border-[#09090B] p-4">
                <div className="text-[10px] font-mono uppercase tracking-widest text-neutral-500 mb-2">Notlar</div>
                <div className="text-sm whitespace-pre-line text-neutral-700">
                  {quote.notes || <span className="text-neutral-400 italic">yok</span>}
                </div>
              </div>
              <div className="border-2 border-[#09090B] p-4 space-y-1 text-right">
                <div className="flex justify-between font-mono">
                  <span className="text-neutral-500">Ara Toplam</span>
                  <span>{fmtCurrency(quote.subtotal)}</span>
                </div>
                <div className="flex justify-between font-mono">
                  <span className="text-neutral-500">KDV %{quote.tax_rate}</span>
                  <span>{fmtCurrency(quote.tax_amount)}</span>
                </div>
                <div className="flex justify-between font-mono text-xl font-bold pt-2 border-t-2 border-[#09090B]">
                  <span>GENEL</span>
                  <span data-testid="quote-detail-total">{fmtCurrency(quote.total)}</span>
                </div>
              </div>
            </div>

            <AuditLogPanel entity="quote" entityId={quote.id} open={open} />
          </div>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            className="rounded-none border-2 border-[#09090B]"
            onClick={() => onOpenChange(false)}
          >
            Kapat
          </Button>
          <Button
            variant="outline"
            className="rounded-none border-2 border-[#09090B]"
            onClick={async () => {
              try {
                const res = await api.get(`/quotes/${quote.id}/pdf`, { responseType: "blob" });
                const url = URL.createObjectURL(res.data);
                const a = document.createElement("a");
                a.href = url; a.download = quotePdfName(quote); a.click();
                URL.revokeObjectURL(url);
              } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
            }}
            data-testid="quote-detail-pdf-btn"
          >
            <FileDown size={16} className="mr-2" /> PDF İndir
          </Button>
          {canEdit && (
            <Button
              variant="outline"
              className="rounded-none border-2 border-[#09090B]"
              onClick={() => { onOpenChange(false); onEdit?.(quote); }}
              data-testid="quote-detail-edit-btn"
            >
              <Pencil size={16} className="mr-2" /> Düzenle
            </Button>
          )}
          {canConvert && (
            <Button
              className="rounded-none bg-[#09090B] hover:bg-[#0055FF] disabled:opacity-50"
              disabled={selectedCount === 0}
              onClick={() => {
                const ids = pendingItems
                  .filter(({ key }) => selectedIds[key])
                  .map(({ it }) => it.id)
                  .filter(Boolean);
                onConvert?.(quote, () => onOpenChange(false), ids);
              }}
              data-testid="quote-detail-convert-btn"
            >
              <ArrowRightCircle size={16} className="mr-2" />
              Seçilenleri Siparişe Dönüştür ({selectedCount}/{pendingItems.length})
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
